/**
 * Centralized tracking IDs for PostHog analytics.
 * Single source of truth for all tracked UI elements.
 *
 * Naming convention: {page/feature}-{section}-{action}
 */

export const TrackingIds = {
  // ============================================
  // BUTTONS
  // ============================================
  buttons: {
    // Header Navigation
    HEADER_SIDEBAR_TOGGLE: 'header-sidebar-toggle',
    HEADER_LOGO_HOME: 'header-logo-home',
    HEADER_NAV_DASHBOARD: 'header-nav-dashboard',
    HEADER_NAV_HOUSEHOLD_PROJECTS: 'header-nav-household-projects',
    HEADER_NAV_MIGRATION_PROJECTS: 'header-nav-migration-projects',
    HEADER_NOTIFICATIONS: 'header-notifications',
    HEADER_SETTINGS: 'header-settings',
    HEADER_LOGOUT: 'header-logout',
    HEADER_MOBILE_LOGO_HOME: 'header-mobile-logo-home',
    HEADER_MOBILE_SIDEBAR_TOGGLE: 'header-mobile-sidebar-toggle',

    // Intro/Onboarding Flow
    INTRO_BACK: 'intro-back',
    INTRO_LOGOUT: 'intro-logout',
    INTRO_GUIDE_CONTINUE: 'intro-guide-continue',
    INTRO_HOW_IT_WORKS_CONTINUE: 'intro-howitworks-continue',
    INTRO_HOWITWORKS_CONTINUE: 'intro-howitworks-continue', // Alias for backwards compatibility
    INTRO_DATA_TRUST_PRIVACY_POLICY: 'intro-datatrust-privacy-policy',
    INTRO_DATA_TRUST_CONTINUE: 'intro-datatrust-continue',
    INTRO_DATATRUST_PRIVACY_POLICY: 'intro-datatrust-privacy-policy', // Alias
    INTRO_DATATRUST_CONTINUE: 'intro-datatrust-continue', // Alias
    INTRO_PRIVACY_CONTINUE: 'intro-privacy-continue',
    INTRO_PRIVACY_VIEW_POLICY: 'intro-privacy-view-policy',
    INTRO_GUIDED_DECISIONS_CONTINUE: 'intro-guided-decisions-continue',

    // Onboarding Chat
    ONBOARDING_BACK: 'onboarding-back',
    ONBOARDING_LOGOUT: 'onboarding-logout',
    ONBOARDING_CHAT_SEND: 'onboarding-chat-send',
    CHAT_MULTISELECT_CONTINUE: 'chat-multiselect-continue',
    CHAT_ACTION: 'chat-action',

    // Sidebar
    SIDEBAR_CLOSE: 'sidebar-close',

    // Household Modal
    HOUSEHOLD_MODAL_SKIP: 'household-modal-skip',
    HOUSEHOLD_MODAL_UPDATE_DETAILS: 'household-modal-update-details',

    // Voice Assistant
    VOICE_ASSISTANT_MUTE_TOGGLE: 'voice-assistant-mute-toggle',
    VOICE_ASSISTANT_SEND_TEXT: 'voice-assistant-send-text',

    // Settings Page - Navigation
    SETTINGS_NAV: 'settings-nav', // Base for dynamic nav buttons
    SETTINGS_NAV_PROFILE: 'settings-nav-profile',
    SETTINGS_NAV_HOUSEHOLD_DETAILS: 'settings-nav-household-details',
    SETTINGS_NAV_HOUSEHOLD: 'settings-nav-household',
    SETTINGS_NAV_NOTIFICATIONS: 'settings-nav-notifications',
    SETTINGS_NAV_VOICE: 'settings-nav-voice',
    SETTINGS_NAV_BILLING: 'settings-nav-billing',
    SETTINGS_NAV_PRIVACY: 'settings-nav-privacy',
    SETTINGS_NAV_SUPPORT: 'settings-nav-support',

    // Settings Page - Actions
    SETTINGS_PROFILE_SAVE: 'settings-profile-save',
    SETTINGS_HOUSEHOLD_UPDATE: 'settings-household-update',
    SETTINGS_HOUSEHOLD_SEND_INVITE: 'settings-household-send-invite',
    SETTINGS_BILLING: 'settings-billing', // Base for dynamic billing buttons
    SETTINGS_BILLING_MONTHLY: 'settings-billing-monthly',
    SETTINGS_BILLING_ANNUAL: 'settings-billing-annual',
    SETTINGS_SUPPORT_KNOWLEDGE_BASE: 'settings-support-knowledge-base',
    SETTINGS_SUPPORT_VIDEO_TUTORIALS: 'settings-support-video-tutorials',

    // Adaptation Projects
    ADAPTATION_NEW_PROJECT: 'adaptation-new-project',
    ADAPTATION_PLAN_LOADING: 'adaptation-plan-loading',
    ADAPTATION_GENERATE_PLAN: 'adaptation-generate-plan',
    ADAPTATION_PLAN_PROCESSING: 'adaptation-plan-processing',
    ADAPTATION_VIEW_DETAILS: 'adaptation-view-details',
    ADAPTATION_UPDATE_PROFILE_SETTINGS: 'adaptation-update-profile-settings',
    ADAPTATION_INVITE_FAMILY: 'adaptation-invite-family',

    // Migration Projects
    MIGRATION_GENERATE_PLAN: 'migration-generate-plan',
    MIGRATION_START_ANALYSIS: 'migration-start-analysis',
    MIGRATION_VIEW_GUIDE: 'migration-view-guide',
  },

  // ============================================
  // INPUTS
  // ============================================
  inputs: {
    // Onboarding Chat
    ONBOARDING_CHAT_INPUT: 'onboarding-chat-input',

    // Voice Assistant
    VOICE_ASSISTANT_TEXT_INPUT: 'voice-assistant-text-input',

    // Settings
    SETTINGS_HOUSEHOLD_INVITE_EMAIL: 'settings-household-invite-email',
  },

  // ============================================
  // TOGGLES / SWITCHES
  // ============================================
  toggles: {
    // Settings - Notifications (dynamic key based on setting.key)
    SETTINGS_NOTIFICATION_PREFIX: 'settings-notification',

    // Settings - Voice
    SETTINGS_VOICE_ENABLED: 'settings-voice-enabled',
    SETTINGS_VOICE_AUTO_LISTEN: 'settings-voice-auto-listen',
  },

  // ============================================
  // EVENTS (PostHog capture events)
  // ============================================
  events: {
    REPORT_GENERATED: 'report_generated',
    PROJECTS_VIEWED: 'projects_viewed',
    PROJECTS_GENERATED: 'projects_generated',
    TASK_COMPLETED: 'task_completed',
    TASK_UNCOMPLETED: 'task_uncompleted',
    TASK_EDITED: 'task_edited',
    DECISION_POINT_SELECTED: 'decision_point_selected',
    // ENG-460: Canonical AI interaction event stream
    DASHBOARD_VIEWED: 'dashboard_viewed',
    CHAT_OPENED: 'chat_opened',
    CHAT_MESSAGE_SENT: 'chat_message_sent',
    VOICE_OPENED: 'voice_opened',
    VOICE_CLOSED: 'voice_closed',
    PROJECT_CREATE_STARTED: 'project_create_started',
    PROJECT_CREATED: 'project_created',
    PROJECT_OPENED: 'project_opened',
    PROJECT_STAGE_CHANGED: 'project_stage_changed',
  },

  // ============================================
  // OTHER ELEMENTS (Clickable areas, modals, etc.)
  // ============================================
  elements: {
    SIDEBAR_BACKDROP_CLOSE: 'sidebar-backdrop-close',
    CHAT_OPTION_SELECT: 'chat-option-select',
  },
} as const;

// ============================================
// TYPE HELPERS
// ============================================
export type ButtonId = (typeof TrackingIds.buttons)[keyof typeof TrackingIds.buttons];
export type InputId = (typeof TrackingIds.inputs)[keyof typeof TrackingIds.inputs];
export type ToggleId = (typeof TrackingIds.toggles)[keyof typeof TrackingIds.toggles];
export type ElementId = (typeof TrackingIds.elements)[keyof typeof TrackingIds.elements];
export type EventId = (typeof TrackingIds.events)[keyof typeof TrackingIds.events];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Creates a dynamic tracking ID by combining a base with additional parts.
 * Useful for dynamic elements like subscription plans, nav items, etc.
 *
 * @example
 * createDynamicId('settings-billing', 'subscribe', 'pro-plan')
 * // Returns: 'settings-billing-subscribe-pro-plan'
 */
export const createDynamicId = (base: string, ...parts: string[]): string => {
  return [base, ...parts.map((p) => p.toLowerCase().replace(/\s+/g, '-'))].join('-');
};

/**
 * Creates a notification toggle ID based on the notification key.
 *
 * @example
 * createNotificationToggleId('riskAlerts')
 * // Returns: 'settings-notification-riskAlerts'
 */
export const createNotificationToggleId = (key: string): string => {
  return `${TrackingIds.toggles.SETTINGS_NOTIFICATION_PREFIX}-${key}`;
};

/**
 * Creates a navigation button ID based on the path.
 *
 * @example
 * createNavButtonId('/dashboard')
 * // Returns: 'header-nav-dashboard'
 */
export const createNavButtonId = (path: string): string => {
  return `header-nav-${path.replace('/', '')}`;
};
